import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { InvestmentProvider } from './context/InvestmentContext';
import { ThemeProvider } from './context/ThemeContext';
import { useInvestmentContext } from './context/InvestmentContext';
import Navigation from './components/Navigation';
import HomeScreen from './screens/HomeScreen';
import GoalsScreen from './screens/GoalsScreen';
import TransactionsScreen from './screens/TransactionsScreen';
import PortfolioScreen from './screens/PortfolioScreen';
import ProfileScreen from './screens/ProfileScreen';
import BrokersScreen from './screens/BrokersScreen';
import AssetsScreen from './screens/AssetsScreen';
import { useAuth } from './hooks/useAuth';

/**
 * Refreshes context data whenever the user navigates to a route,
 * ensuring each screen always shows up-to-date information.
 */
function RouteDataSync() {
  const { pathname } = useLocation();
  const { fetchGoals, fetchBrokers, fetchAssets, fetchPortfolio } = useInvestmentContext();

  React.useEffect(() => {
    switch (pathname) {
      case '/goals':     fetchGoals();     break;
      case '/brokers':   fetchBrokers();   break;
      case '/assets':    fetchAssets();    break;
      case '/portfolio': fetchPortfolio(); break;
      default: break;
    }
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function LoginPage() {
  const { login, loading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = React.useState({ username: '', password: '' });

  React.useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(form.username, form.password);
    if (result.success) navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Investment Tracker</h1>
        <p className="text-gray-500 mb-8">Sign in to manage your portfolio</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="Enter your username"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="Enter your password"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Don&apos;t have an account?{' '}
          <a href="/register" className="text-blue-600 hover:underline font-medium">
            Register
          </a>
        </p>
      </div>
    </div>
  );
}

function RegisterPage() {
  const { register, loading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = React.useState({
    username: '', email: '', password: '', password2: '',
    first_name: '', last_name: '',
  });
  const [fieldErrors, setFieldErrors] = React.useState({});

  React.useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    const result = await register(form);
    if (result.success) {
      navigate('/', { replace: true });
    } else if (result.errors) {
      setFieldErrors(result.errors);
    }
  };

  const field = (name, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        placeholder={placeholder}
        required={['username', 'password', 'password2'].includes(name)}
      />
      {fieldErrors[name] && (
        <p className="mt-1 text-xs text-red-600">{fieldErrors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
        <p className="text-gray-500 mb-8">Start tracking your investments</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {field('first_name', 'First Name', 'text', 'John')}
            {field('last_name', 'Last Name', 'text', 'Doe')}
          </div>
          {field('username', 'Username', 'text', 'johndoe')}
          {field('email', 'Email', 'email', 'john@example.com')}
          {field('password', 'Password', 'password', 'Min 8 characters')}
          {field('password2', 'Confirm Password', 'password', 'Repeat password')}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <a href="/login" className="text-blue-600 hover:underline font-medium">
            Sign In
          </a>
        </p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <InvestmentProvider>
              <div className="min-h-screen bg-gray-50">
                <RouteDataSync />
                <Routes>
                  <Route path="/" element={<HomeScreen />} />
                  <Route path="/goals" element={<GoalsScreen />} />
                  <Route path="/transactions" element={<TransactionsScreen />} />
                  <Route path="/portfolio" element={<PortfolioScreen />} />
                  <Route path="/brokers" element={<BrokersScreen />} />
                  <Route path="/assets" element={<AssetsScreen />} />
                  <Route path="/profile" element={<ProfileScreen />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Navigation />
              </div>
            </InvestmentProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}
